import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Divider,
  Tabs,
  Tab,
  CircularProgress,
  TablePagination,
  Tooltip,
  Badge,
  TableSortLabel
} from '@mui/material';
import {
  Edit as EditIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  BookOnline as BookingIcon,
  CheckCircle as ConfirmedIcon,
  Schedule as PendingIcon,
  Cancel as CancelledIcon,
  Done as CompletedIcon,
  Payment as PaymentIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Notes as NotesIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  CurrencyLira as CurrencyLiraIcon
} from '@mui/icons-material';
import { bookingService } from '../../services/firebase';
import type { Booking } from '../../types';
import { Timestamp } from 'firebase/firestore';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`booking-tabpanel-${index}`}
      aria-labelledby={`booking-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ManageBookingsPage: React.FC = () => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [filteredBookings, setFilteredBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [paymentFilter, setPaymentFilter] = useState<string>('all');
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Dialog states
  const [viewDialog, setViewDialog] = useState<{
    open: boolean;
    booking: Booking | null;
  }>({ open: false, booking: null });

  const [editDialog, setEditDialog] = useState<{
    open: boolean;
    booking: Booking | null;
  }>({ open: false, booking: null });

  // Sorting states
  const [sortField, setSortField] = useState<keyof Booking>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    fetchBookings();
  }, []);

  useEffect(() => {
    filterBookings();
  }, [bookings, searchTerm, statusFilter, paymentFilter, sortField, sortDirection]);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const bookingsData = await bookingService.getAllBookings();
      // Sort by creation date (newest first)
      const sortedBookings = bookingsData.sort((a, b) => {
        const getTimestamp = (timestamp: any) => {
          if (!timestamp) return 0;
          if (timestamp.toMillis && typeof timestamp.toMillis === 'function') {
            return timestamp.toMillis();
          }
          if (timestamp.seconds) {
            return timestamp.seconds * 1000;
          }
          if (timestamp instanceof Date) {
            return timestamp.getTime();
          }
          if (typeof timestamp === 'number') {
            return timestamp;
          }
          return 0;
        };
        
        const dateA = getTimestamp(a.createdAt);
        const dateB = getTimestamp(b.createdAt);
        return dateB - dateA;
      });
      setBookings(sortedBookings);
    } catch (err) {
      setError('Rezervasyonlar yüklenirken hata oluştu');
      console.error('Error fetching bookings:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterBookings = () => {
    let filtered = bookings;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(booking =>
        booking.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.customerEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.customerPhone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.tourName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.id?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(booking => booking.status === statusFilter);
    }

    // Payment filter
    if (paymentFilter !== 'all') {
      filtered = filtered.filter(booking => booking.paymentStatus === paymentFilter);
    }

    // Apply sorting
    filtered = filtered.sort((a, b) => {
      const getValue = (booking: Booking, field: keyof Booking) => {
        const value = booking[field];
        
        // Handle timestamp values
        if (field === 'createdAt' || field === 'updatedAt' || field === 'tourDate' || field === 'bookingDate' || field === 'paymentDate') {
          if (!value) return 0;
          if (value instanceof Timestamp) return value.toMillis();
          if (value instanceof Date) return value.getTime();
          // Fallback for objects that might be Timestamps
          if (typeof value === 'object' && value && 'seconds' in value && typeof (value as any).seconds === 'number') {
            return (value as any).seconds * 1000;
          }
          return 0;
        }
        
        if (field === 'tourDetails') {
          return (value as Booking['tourDetails'])?.destination.toLowerCase() || '';
        }

        // Handle numeric values
        if (field === 'totalPrice' || field === 'participants') {
          return Number(value) || 0;
        }
        
        // Handle string values
        return String(value || '').toLowerCase();
      };
      
      const aValue = getValue(a, sortField);
      const bValue = getValue(b, sortField);
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredBookings(filtered);
  };

  const handleSort = (field: keyof Booking) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleUpdateBookingStatus = async (bookingId: string, status: Booking['status'], paymentStatus?: Booking['paymentStatus']) => {
    try {
      setUpdating(true);
      await bookingService.updateBookingStatus(bookingId, status, paymentStatus);
      
      // Update local state
      setBookings(prevBookings =>
        prevBookings.map(booking =>
          booking.id === bookingId 
            ? { 
                ...booking, 
                status, 
                ...(paymentStatus && { paymentStatus }),
                updatedAt: Timestamp.now()
              } 
            : booking
        )
      );
      
      setSuccessMessage('Rezervasyon durumu başarıyla güncellendi');
      setEditDialog({ open: false, booking: null });
    } catch (err) {
      setError('Rezervasyon güncellenirken hata oluştu');
      console.error('Error updating booking:', err);
    } finally {
      setUpdating(false);
    }
  };

  const getStatusColor = (status: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (status) {
      case 'confirmed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      case 'completed':
        return 'info';
      default:
        return 'default';
    }
  };

  const getPaymentStatusColor = (status: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'refunded':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'confirmed':
        return <ConfirmedIcon />;
      case 'pending':
        return <PendingIcon />;
      case 'cancelled':
        return <CancelledIcon />;
      case 'completed':
        return <CompletedIcon />;
      default:
        return <BookingIcon />;
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Bilinmiyor';
    
    try {
      let date: Date;
      
      // Firestore Timestamp object
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
      }
      // Already a Date object
      else if (timestamp instanceof Date) {
        date = timestamp;
      }
      // Timestamp with seconds and nanoseconds
      else if (timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      }
      // Unix timestamp
      else if (typeof timestamp === 'number') {
        date = new Date(timestamp);
      }
      // String date
      else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      }
      else {
        return 'Geçersiz tarih';
      }
      
      return date.toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Date formatting error:', error, timestamp);
      return 'Tarih hatası';
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(price);
  };

  const getStatsForTab = (tabIndex: number) => {
    switch (tabIndex) {
      case 0:
        return filteredBookings.length;
      case 1:
        return filteredBookings.filter(booking => booking.status === 'pending').length;
      case 2:
        return filteredBookings.filter(booking => booking.status === 'confirmed').length;
      case 3:
        return filteredBookings.filter(booking => booking.status === 'completed').length;
      case 4:
        return filteredBookings.filter(booking => booking.status === 'cancelled').length;
      default:
        return 0;
    }
  };

  const getTotalRevenue = () => {
    return bookings
      .filter(booking => booking.paymentStatus === 'paid')
      .reduce((total, booking) => total + booking.totalPrice, 0);
  };

  const getPendingPayments = () => {
    return bookings
      .filter(booking => booking.paymentStatus === 'pending')
      .reduce((total, booking) => total + booking.totalPrice, 0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Rezervasyon Yönetimi
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchBookings}
            disabled={loading}
          >
            Yenile
          </Button>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={() => window.print()}
          >
            Yazdır
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
        <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <BookingIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Toplam Rezervasyon
                  </Typography>
                  <Typography variant="h4">
                    {bookings.length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PendingIcon sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Bekleyen
                  </Typography>
                  <Typography variant="h4">
                    {bookings.filter(b => b.status === 'pending').length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CurrencyLiraIcon sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Toplam Gelir
                  </Typography>
                  <Typography variant="h4">
                    {formatPrice(getTotalRevenue())}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PaymentIcon sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Bekleyen Ödemeler
                  </Typography>
                  <Typography variant="h4">
                    {formatPrice(getPendingPayments())}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
              <TextField
                fullWidth
                label="Rezervasyon Ara"
                variant="outlined"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Ad, email, telefon, tur adı veya rezervasyon ID..."
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
                }}
              />
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
              <FormControl fullWidth>
                <InputLabel>Durum Filtresi</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">Tüm Durumlar</MenuItem>
                  <MenuItem value="pending">Bekleyen</MenuItem>
                  <MenuItem value="confirmed">Onaylı</MenuItem>
                  <MenuItem value="completed">Tamamlanan</MenuItem>
                  <MenuItem value="cancelled">İptal Edilen</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
              <FormControl fullWidth>
                <InputLabel>Ödeme Durumu</InputLabel>
                <Select
                  value={paymentFilter}
                  onChange={(e) => setPaymentFilter(e.target.value)}
                >
                  <MenuItem value="all">Tüm Ödemeler</MenuItem>
                  <MenuItem value="pending">Bekleyen</MenuItem>
                  <MenuItem value="paid">Ödendi</MenuItem>
                  <MenuItem value="refunded">İade Edildi</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab 
            label={
              <Badge badgeContent={getStatsForTab(0)} color="primary">
                Tümü
              </Badge>
            } 
          />
          <Tab 
            label={
              <Badge badgeContent={getStatsForTab(1)} color="warning">
                Bekleyen
              </Badge>
            } 
          />
          <Tab 
            label={
              <Badge badgeContent={getStatsForTab(2)} color="success">
                Onaylı
              </Badge>
            } 
          />
          <Tab 
            label={
              <Badge badgeContent={getStatsForTab(3)} color="info">
                Tamamlanan
              </Badge>
            } 
          />
          <Tab 
            label={
              <Badge badgeContent={getStatsForTab(4)} color="error">
                İptal Edilen
              </Badge>
            } 
          />
        </Tabs>
      </Box>

      {/* Bookings Table */}
      <TabPanel value={tabValue} index={0}>
                        <BookingsTable
                  bookings={filteredBookings.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)}
                  onView={(booking) => setViewDialog({ open: true, booking })}
                  onEdit={(booking) => setEditDialog({ open: true, booking })}
                  getStatusColor={getStatusColor}
                  getPaymentStatusColor={getPaymentStatusColor}
                  getStatusIcon={getStatusIcon}
                  formatDate={formatDate}
                  formatPrice={formatPrice}
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
                      <BookingsTable
                bookings={filteredBookings.filter(b => b.status === 'pending').slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)}
                onView={(booking) => setViewDialog({ open: true, booking })}
                onEdit={(booking) => setEditDialog({ open: true, booking })}
                getStatusColor={getStatusColor}
                getPaymentStatusColor={getPaymentStatusColor}
                getStatusIcon={getStatusIcon}
                formatDate={formatDate}
                formatPrice={formatPrice}
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
                      <BookingsTable
                bookings={filteredBookings.filter(b => b.status === 'confirmed').slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)}
                onView={(booking) => setViewDialog({ open: true, booking })}
                onEdit={(booking) => setEditDialog({ open: true, booking })}
                getStatusColor={getStatusColor}
                getPaymentStatusColor={getPaymentStatusColor}
                getStatusIcon={getStatusIcon}
                formatDate={formatDate}
                formatPrice={formatPrice}
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
            </TabPanel>

            <TabPanel value={tabValue} index={3}>
              <BookingsTable
                bookings={filteredBookings.filter(b => b.status === 'completed').slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)}
                onView={(booking) => setViewDialog({ open: true, booking })}
                onEdit={(booking) => setEditDialog({ open: true, booking })}
                getStatusColor={getStatusColor}
                getPaymentStatusColor={getPaymentStatusColor}
                getStatusIcon={getStatusIcon}
                formatDate={formatDate}
                formatPrice={formatPrice}
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
            </TabPanel>

            <TabPanel value={tabValue} index={4}>
              <BookingsTable
                bookings={filteredBookings.filter(b => b.status === 'cancelled').slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)}
                onView={(booking) => setViewDialog({ open: true, booking })}
                onEdit={(booking) => setEditDialog({ open: true, booking })}
                getStatusColor={getStatusColor}
                getPaymentStatusColor={getPaymentStatusColor}
                getStatusIcon={getStatusIcon}
                formatDate={formatDate}
                formatPrice={formatPrice}
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
      </TabPanel>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={filteredBookings.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Sayfa başına satır:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
      />

      {/* View Booking Dialog */}
      <ViewBookingDialog
        open={viewDialog.open}
        booking={viewDialog.booking}
        onClose={() => setViewDialog({ open: false, booking: null })}
        formatDate={formatDate}
        formatPrice={formatPrice}
        getStatusIcon={getStatusIcon}
        getStatusColor={getStatusColor}
        getPaymentStatusColor={getPaymentStatusColor}
      />

      {/* Edit Booking Dialog */}
      <EditBookingDialog
        open={editDialog.open}
        booking={editDialog.booking}
        onClose={() => setEditDialog({ open: false, booking: null })}
        onSave={handleUpdateBookingStatus}
        updating={updating}
      />
    </Container>
  );
};

// Bookings Table Component
const BookingsTable: React.FC<{
  bookings: Booking[];
  onView: (booking: Booking) => void;
  onEdit: (booking: Booking) => void;
  getStatusColor: (status: string) => "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning";
  getPaymentStatusColor: (status: string) => "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning";
  getStatusIcon: (status: string) => React.ReactElement;
  formatDate: (timestamp: any) => string;
  formatPrice: (price: number) => string;
  sortField: keyof Booking;
  sortDirection: 'asc' | 'desc';
  onSort: (field: keyof Booking) => void;
}> = ({ bookings, onView, onEdit, getStatusColor, getPaymentStatusColor, getStatusIcon, formatDate, formatPrice, sortField, sortDirection, onSort }) => {
  return (
    <TableContainer component={Paper} sx={{ mt: 2, overflowX: 'auto' }}>
      <Table sx={{ minWidth: 1200 }}>
        <TableHead>
          <TableRow>
            <TableCell>
              <TableSortLabel
                active={sortField === 'id'}
                direction={sortField === 'id' ? sortDirection : 'asc'}
                onClick={() => onSort('id')}
              >
                Rezervasyon ID
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortField === 'customerName'}
                direction={sortField === 'customerName' ? sortDirection : 'asc'}
                onClick={() => onSort('customerName')}
              >
                Müşteri Adı
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortField === 'tourName'}
                direction={sortField === 'tourName' ? sortDirection : 'asc'}
                onClick={() => onSort('tourName')}
              >
                Tur Adı
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortField === 'tourDate'}
                direction={sortField === 'tourDate' ? sortDirection : 'asc'}
                onClick={() => onSort('tourDate')}
              >
                Tur Tarihi
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortField === 'participants'}
                direction={sortField === 'participants' ? sortDirection : 'asc'}
                onClick={() => onSort('participants')}
              >
                Kişi Sayısı
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortField === 'totalPrice'}
                direction={sortField === 'totalPrice' ? sortDirection : 'asc'}
                onClick={() => onSort('totalPrice')}
              >
                Toplam Tutar
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortField === 'status'}
                direction={sortField === 'status' ? sortDirection : 'asc'}
                onClick={() => onSort('status')}
              >
                Rezervasyon Durumu
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortField === 'paymentStatus'}
                direction={sortField === 'paymentStatus' ? sortDirection : 'asc'}
                onClick={() => onSort('paymentStatus')}
              >
                Ödeme Durumu
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortField === 'createdAt'}
                direction={sortField === 'createdAt' ? sortDirection : 'asc'}
                onClick={() => onSort('createdAt')}
              >
                Oluşturma Tarihi
              </TableSortLabel>
            </TableCell>
            <TableCell>İşlemler</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {bookings.map(booking => (
            <TableRow key={booking.id} hover>
              <TableCell>
                <Tooltip title={booking.id}>
                  <Typography variant="body2" noWrap sx={{ maxWidth: '100px' }}>
                    {booking.id}
                  </Typography>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {booking.customerName}
                  {booking.isGuestBooking && (
                    <Chip 
                      label="Misafir" 
                      size="small" 
                      color="warning" 
                      sx={{ ml: 1, fontSize: '0.7rem' }}
                    />
                  )}
                </Typography>
                <Typography variant="caption" color="textSecondary">{booking.customerEmail}</Typography>
              </TableCell>
              <TableCell>{booking.tourName}</TableCell>
              <TableCell>{formatDate(booking.tourDate)}</TableCell>
              <TableCell>
                <Typography variant="body2">
                  {booking.participants} kişi
                </Typography>
              </TableCell>
              <TableCell>{formatPrice(booking.totalPrice)}</TableCell>
              <TableCell>
                <Chip
                  icon={getStatusIcon(booking.status)}
                  label={booking.status === 'pending' ? 'Bekleyen' : 
                        booking.status === 'confirmed' ? 'Onaylı' :
                        booking.status === 'completed' ? 'Tamamlandı' :
                        booking.status === 'cancelled' ? 'İptal' : booking.status}
                  color={getStatusColor(booking.status)}
                  size="small"
                />
              </TableCell>
              <TableCell>
                <Chip
                  label={booking.paymentStatus === 'pending' ? 'Bekleyen' :
                        booking.paymentStatus === 'paid' ? 'Ödendi' :
                        booking.paymentStatus === 'refunded' ? 'İade' : booking.paymentStatus}
                  color={getPaymentStatusColor(booking.paymentStatus)}
                  size="small"
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {formatDate(booking.createdAt)}
                </Typography>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Görüntüle">
                    <IconButton
                      size="small"
                      onClick={() => onView(booking)}
                    >
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Düzenle">
                    <IconButton
                      size="small"
                      onClick={() => onEdit(booking)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
          {bookings.length === 0 && (
            <TableRow>
              <TableCell colSpan={10} align="center">
                <Typography variant="body2" color="textSecondary">
                  Rezervasyon bulunamadı
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// View Booking Dialog Component
const ViewBookingDialog: React.FC<{
  open: boolean;
  booking: Booking | null;
  onClose: () => void;
  formatDate: (timestamp: any) => string;
  formatPrice: (price: number) => string;
  getStatusIcon: (status: string) => React.ReactElement;
  getStatusColor: (status: string) => "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning";
  getPaymentStatusColor: (status: string) => "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning";
}> = ({ open, booking, onClose, formatDate, formatPrice, getStatusIcon, getStatusColor, getPaymentStatusColor }) => {
  if (!booking) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <BookingIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant="h6">Rezervasyon Detayları</Typography>
            <Typography variant="body2" color="textSecondary">
              #{booking.id.slice(-8)}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {/* Customer Information */}
          <Box sx={{ flex: '1 1 400px', minWidth: 400 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon /> Müşteri Bilgileri
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <strong>Ad Soyad:</strong> {booking.customerName}
                    {booking.isGuestBooking && (
                      <Chip 
                        label="Misafir Rezervasyonu" 
                        size="small" 
                        color="warning" 
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Typography>
                  <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon fontSize="small" />
                    <strong>E-posta:</strong> {booking.customerEmail}
                  </Typography>
                  <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneIcon fontSize="small" />
                    <strong>Telefon:</strong> {booking.customerPhone}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Booking Information */}
          <Box sx={{ flex: '1 1 400px', minWidth: 400 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarIcon /> Rezervasyon Bilgileri
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography><strong>Tur:</strong> {booking.tourName}</Typography>
                  <Typography><strong>Tarih:</strong> {formatDate(booking.tourDate)}</Typography>
                  <Typography><strong>Kişi Sayısı:</strong> {booking.participants} kişi</Typography>
                  <Typography><strong>Toplam Tutar:</strong> {formatPrice(booking.totalPrice)}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Status Information */}
          <Box sx={{ flex: '1 1 400px', minWidth: 400 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Durum Bilgileri
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <strong>Rezervasyon Durumu:</strong>
                    <Chip
                      icon={getStatusIcon(booking.status)}
                      label={booking.status === 'pending' ? 'Bekleyen' : 
                            booking.status === 'confirmed' ? 'Onaylı' :
                            booking.status === 'completed' ? 'Tamamlandı' :
                            booking.status === 'cancelled' ? 'İptal' : booking.status}
                      color={getStatusColor(booking.status)}
                      size="small"
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <strong>Ödeme Durumu:</strong>
                    <Chip
                      label={booking.paymentStatus === 'pending' ? 'Bekleyen' :
                            booking.paymentStatus === 'paid' ? 'Ödendi' :
                            booking.paymentStatus === 'refunded' ? 'İade' : booking.paymentStatus}
                      color={getPaymentStatusColor(booking.paymentStatus)}
                      size="small"
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Dates and Notes */}
          <Box sx={{ flex: '1 1 400px', minWidth: 400 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <NotesIcon /> Ek Bilgiler
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography><strong>Oluşturma:</strong> {formatDate(booking.createdAt)}</Typography>
                  <Typography><strong>Güncelleme:</strong> {formatDate(booking.updatedAt)}</Typography>
                  {booking.paymentDate && (
                    <Typography><strong>Ödeme Tarihi:</strong> {formatDate(booking.paymentDate)}</Typography>
                  )}
                  {booking.specialRequests && (
                    <Box>
                      <Typography><strong>Özel İstekler:</strong></Typography>
                      <Typography variant="body2" sx={{ mt: 1, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                        {booking.specialRequests}
                      </Typography>
                    </Box>
                  )}
                  {booking.adminNotes && (
                    <Box>
                      <Typography><strong>Admin Notları:</strong></Typography>
                      <Typography variant="body2" sx={{ mt: 1, p: 2, bgcolor: 'warning.50', borderRadius: 1 }}>
                        {booking.adminNotes}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Kapat</Button>
        <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => window.print()}>
          Yazdır
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Edit Booking Dialog Component
const EditBookingDialog: React.FC<{
  open: boolean;
  booking: Booking | null;
  onClose: () => void;
  onSave: (bookingId: string, status: Booking['status'], paymentStatus?: Booking['paymentStatus']) => void;
  updating: boolean;
}> = ({ open, booking, onClose, onSave, updating }) => {
  const [status, setStatus] = useState<Booking['status']>('pending');
  const [paymentStatus, setPaymentStatus] = useState<Booking['paymentStatus']>('pending');
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    if (booking) {
      setStatus(booking.status);
      setPaymentStatus(booking.paymentStatus);
      setAdminNotes(booking.adminNotes || '');
    }
  }, [booking]);

  const handleSave = () => {
    if (booking) {
      onSave(booking.id, status, paymentStatus);
    }
  };

  if (!booking) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Rezervasyon Düzenle</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Typography variant="body2" color="textSecondary">
            Rezervasyon ID: #{booking.id.slice(-8)}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Müşteri: {booking.customerName} - {booking.tourName}
          </Typography>
          
          <FormControl fullWidth>
            <InputLabel>Rezervasyon Durumu</InputLabel>
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value as Booking['status'])}
            >
              <MenuItem value="pending">Bekleyen</MenuItem>
              <MenuItem value="confirmed">Onaylı</MenuItem>
              <MenuItem value="completed">Tamamlandı</MenuItem>
              <MenuItem value="cancelled">İptal Edildi</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Ödeme Durumu</InputLabel>
            <Select
              value={paymentStatus}
              onChange={(e) => setPaymentStatus(e.target.value as Booking['paymentStatus'])}
            >
              <MenuItem value="pending">Bekleyen</MenuItem>
              <MenuItem value="paid">Ödendi</MenuItem>
              <MenuItem value="refunded">İade Edildi</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Admin Notları"
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            multiline
            rows={3}
            fullWidth
            placeholder="Bu rezervasyon hakkında notlarınız..."
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={updating}>
          İptal
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={updating}>
          {updating ? 'Kaydediliyor...' : 'Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManageBookingsPage;